import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { firestore } from "../firebase";

const text = (value) => String(value ?? "").trim();

function toDateValue(value) {
  if (!value) return null;
  if (value?.toDate) return value;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function newestFirst(rows) {
  return [...rows].sort((a, b) => {
    const aDate = a.publishedAt?.toDate?.() || a.updatedAt?.toDate?.() || toDateValue(a.publishedAt) || toDateValue(a.updatedAt) || new Date(0);
    const bDate = b.publishedAt?.toDate?.() || b.updatedAt?.toDate?.() || toDateValue(b.publishedAt) || toDateValue(b.updatedAt) || new Date(0);
    return bDate.getTime() - aDate.getTime();
  });
}

export function subscribeClientPortalWork(clientId, onData, onError) {
  if (!clientId) {
    onData([]);
    return () => {};
  }

  const q = query(
    collection(firestore, "clientPortalWork"),
    where("clientId", "==", clientId),
    where("visible", "==", true)
  );

  return onSnapshot(
    q,
    (snapshot) => onData(newestFirst(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })))),
    onError
  );
}

export function subscribeClientPortalUpdates(clientId, onData, onError) {
  if (!clientId) {
    onData([]);
    return () => {};
  }

  const q = query(
    collection(firestore, "clientPortalUpdates"),
    where("clientId", "==", clientId),
    where("visible", "==", true)
  );

  return onSnapshot(
    q,
    (snapshot) => onData(newestFirst(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })))),
    onError
  );
}

export async function publishMatterToClient(matter, values, actor) {
  if (!matter?.id) throw new Error("Matter could not be identified.");
  if (!matter?.clientId) throw new Error("This Matter is not linked to a Client.");

  const clientStatus = text(values?.clientStatus);
  const clientUpdate = text(values?.clientUpdate);
  const nextAction = text(values?.nextAction);

  if (!clientStatus) throw new Error("Client Status is required before publishing.");
  if (!clientUpdate) throw new Error("Client Update is required before publishing.");

  const workRef = doc(firestore, "clientPortalWork", `matter_${matter.id}`);
  const updateRef = doc(collection(firestore, "clientPortalUpdates"));
  const matterRef = doc(firestore, "matters", matter.id);
  const batch = writeBatch(firestore);

  const safePayload = {
    firmId: text(matter.firmId),
    clientId: matter.clientId,
    sourceType: "matter",
    sourceId: matter.id,
    reference: text(matter.matterCode),
    title: text(matter.title || matter.assignmentType || matter.matterCode || "Matter"),
    clientStatus,
    clientUpdate,
    nextAction,
    nextActionDate: toDateValue(values?.nextActionDate || matter.nextActionDate),
    hearingDate: toDateValue(values?.hearingDate || matter.nextHearingDate),
    visible: true,
    updatedAt: serverTimestamp(),
    publishedAt: serverTimestamp(),
    publishedById: actor?.id || "",
    publishedByName: actor?.name || actor?.username || actor?.email || "",
  };

  batch.set(workRef, safePayload, { merge: true });
  batch.set(updateRef, safePayload);
  batch.update(matterRef, {
    clientVisible: true,
    clientStatus,
    clientUpdate,
    clientPublishedAt: serverTimestamp(),
    clientPublishedById: actor?.id || "",
    clientPublishedByName: actor?.name || actor?.username || actor?.email || "",
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
}

export async function hideMatterFromClient(matter, actor) {
  if (!matter?.id) throw new Error("Matter could not be identified.");

  const updatesQuery = query(
    collection(firestore, "clientPortalUpdates"),
    where("sourceType", "==", "matter"),
    where("sourceId", "==", matter.id)
  );
  const updateSnapshot = await getDocs(updatesQuery);

  const batch = writeBatch(firestore);
  const workRef = doc(firestore, "clientPortalWork", `matter_${matter.id}`);
  const matterRef = doc(firestore, "matters", matter.id);

  batch.set(workRef, {
    clientId: matter.clientId || "",
    sourceType: "matter",
    sourceId: matter.id,
    visible: false,
    updatedAt: serverTimestamp(),
    hiddenAt: serverTimestamp(),
    hiddenById: actor?.id || "",
    hiddenByName: actor?.name || actor?.username || actor?.email || "",
  }, { merge: true });

  updateSnapshot.docs.forEach((item) => {
    batch.update(item.ref, {
      visible: false,
      hiddenAt: serverTimestamp(),
      hiddenById: actor?.id || "",
      hiddenByName: actor?.name || actor?.username || actor?.email || "",
    });
  });

  batch.update(matterRef, {
    clientVisible: false,
    clientHiddenAt: serverTimestamp(),
    clientHiddenById: actor?.id || "",
    clientHiddenByName: actor?.name || actor?.username || actor?.email || "",
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
}
